import * as THREE from 'three';

export type TrafficLightPhase = 'RED' | 'RED_YELLOW' | 'GREEN' | 'YELLOW';

export class TrafficLight {
  public group: THREE.Group;
  public phase: TrafficLightPhase = 'RED';
  public timer: number = 0;

  private redMesh!: THREE.Mesh;
  private yellowMesh!: THREE.Mesh;
  private greenMesh!: THREE.Mesh;

  // Overhead cantilever secondary signal head
  private overheadRedMesh!: THREE.Mesh;
  private overheadYellowMesh!: THREE.Mesh;
  private overheadGreenMesh!: THREE.Mesh;

  private signalGlowLight!: THREE.PointLight;

  public readonly stopLineZ: number;
  public readonly direction: THREE.Vector3;
  public readonly approachDirection: THREE.Vector3;

  private redLenses: THREE.Mesh[] = [];
  private yellowLenses: THREE.Mesh[] = [];
  private greenLenses: THREE.Mesh[] = [];

  constructor(position: THREE.Vector3, rotationY: number = 0, initialPhaseOffset: number = 0) {
    this.group = new THREE.Group();
    this.group.position.copy(position);
    this.group.rotation.y = rotationY;
    this.timer = initialPhaseOffset;
    this.stopLineZ = position.z;
    this.direction = new THREE.Vector3(0, 0, 1).applyAxisAngle(new THREE.Vector3(0, 1, 0), rotationY);
    this.approachDirection = this.direction.clone().negate();

    this.buildMesh();
    this.updateLights();
  }

  private buildMesh() {
    const mastMat = new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.8, roughness: 0.3 });
    const housingMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.8 });
    const visorMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.9 });

    // 1. Heavy Steel Vertical Mast (Height: 5.4m)
    const poleGeom = new THREE.CylinderGeometry(0.09, 0.12, 5.4, 16);
    const pole = new THREE.Mesh(poleGeom, mastMat);
    pole.position.y = 2.7;
    pole.castShadow = true;
    this.group.add(pole);

    // 2. Overhead Cantilever Arm (Peitschenausleger extending 2.8m over the roadway)
    const armGeom = new THREE.CylinderGeometry(0.06, 0.08, 3.2, 12);
    armGeom.rotateZ(Math.PI / 2);
    const arm = new THREE.Mesh(armGeom, mastMat);
    arm.position.set(-1.4, 5.1, 0);
    this.group.add(arm);

    // ── Primary Roadside Signal Housing (Height ~3.0m) ──────────────────────
    const roadsideHead = this.createSignalHead(housingMat, visorMat, false);
    roadsideHead.position.set(0, 3.0, 0.22);
    this.group.add(roadsideHead);

    // ── Secondary Overhead Signal Housing (Suspended above the lane at y=4.8m)
    const overheadHead = this.createSignalHead(housingMat, visorMat, true);
    overheadHead.position.set(-2.4, 4.8, 0.22);
    this.group.add(overheadHead);

    // ── Driver Eye-Level Repeat Signal Housing (Height ~1.35m) ───────────────
    // Standard German Nahsignalgeber at eye-level so drivers stopped right at the stop line can clearly see signals through lower windshield
    const driverHead = this.createSignalHead(housingMat, visorMat, false);
    driverHead.position.set(0, 1.35, 0.22);
    driverHead.scale.set(0.72, 0.72, 0.72);
    this.group.add(driverHead);

    // ── Dynamic Signal Glow Light ──────────────────────────────────────────
    this.signalGlowLight = new THREE.PointLight(0xef4444, 4.0, 18, 1.2);
    this.signalGlowLight.position.set(-1.2, 4.0, 1.2);
    this.group.add(this.signalGlowLight);
  }

  /**
   * Adds a secondary / far-side repeat head across the intersection or on the opposite curb.
   * Lenses update in 100% synchronized lockstep with this traffic light instance.
   */
  public addSecondaryHead(worldPos: THREE.Vector3, rotationY: number) {
    const localPos = worldPos.clone().sub(this.group.position);
    localPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), -this.group.rotation.y);

    const mastMat = new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.8, roughness: 0.3 });
    const housingMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.8 });
    const visorMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.9 });

    const postGroup = new THREE.Group();
    postGroup.position.copy(localPos);
    postGroup.rotation.y = rotationY - this.group.rotation.y;

    // 3.2m Galvanized steel mast
    const poleGeom = new THREE.CylinderGeometry(0.065, 0.085, 3.2, 12);
    const pole = new THREE.Mesh(poleGeom, mastMat);
    pole.position.y = 1.6;
    pole.castShadow = true;
    postGroup.add(pole);

    // Primary signal head at eye height (y = 2.4m)
    const head = this.createSignalHead(housingMat, visorMat, false);
    head.position.set(0, 2.4, 0.22);
    postGroup.add(head);

    // Eye-level Nahsignalgeber at y = 1.35m
    const nahHead = this.createSignalHead(housingMat, visorMat, false);
    nahHead.position.set(0, 1.35, 0.22);
    nahHead.scale.set(0.72, 0.72, 0.72);
    postGroup.add(nahHead);

    this.group.add(postGroup);
  }

  private createSignalHead(housingMat: THREE.Material, visorMat: THREE.Material, isOverhead: boolean): THREE.Group {
    const head = new THREE.Group();

    // Black signal box
    const boxGeom = new THREE.BoxGeometry(0.42, 1.15, 0.30);
    const box = new THREE.Mesh(boxGeom, housingMat);
    head.add(box);

    // Lens geometry
    const lensGeom = new THREE.CylinderGeometry(0.10, 0.10, 0.05, 20);
    lensGeom.rotateX(Math.PI / 2);

    // Front Lenses (Facing +Z)
    const redMesh = new THREE.Mesh(lensGeom, new THREE.MeshStandardMaterial({ color: 0x450a0a }));
    redMesh.position.set(0, 0.36, 0.16);

    const yellowMesh = new THREE.Mesh(lensGeom, new THREE.MeshStandardMaterial({ color: 0x422006 }));
    yellowMesh.position.set(0, 0.0, 0.16);

    const greenMesh = new THREE.Mesh(lensGeom, new THREE.MeshStandardMaterial({ color: 0x052e16 }));
    greenMesh.position.set(0, -0.36, 0.16);

    // Rear Lenses (Facing -Z) — guarantees visible illuminated lights from ALL angles
    const rearRed = new THREE.Mesh(lensGeom, new THREE.MeshStandardMaterial({ color: 0x450a0a }));
    rearRed.position.set(0, 0.36, -0.16);
    rearRed.rotation.y = Math.PI;

    const rearYellow = new THREE.Mesh(lensGeom, new THREE.MeshStandardMaterial({ color: 0x422006 }));
    rearYellow.position.set(0, 0.0, -0.16);
    rearYellow.rotation.y = Math.PI;

    const rearGreen = new THREE.Mesh(lensGeom, new THREE.MeshStandardMaterial({ color: 0x052e16 }));
    rearGreen.position.set(0, -0.36, -0.16);
    rearGreen.rotation.y = Math.PI;

    // Anti-glare hoods / sun visors (Schute)
    const visorGeom = new THREE.CylinderGeometry(0.12, 0.12, 0.14, 16, 1, true, 0, Math.PI);
    visorGeom.rotateX(Math.PI / 2);

    const visorR = new THREE.Mesh(visorGeom, visorMat);
    visorR.position.set(0, 0.36, 0.22);
    const visorY = new THREE.Mesh(visorGeom, visorMat);
    visorY.position.set(0, 0.0, 0.22);
    const visorG = new THREE.Mesh(visorGeom, visorMat);
    visorG.position.set(0, -0.36, 0.22);

    const rearVisorR = new THREE.Mesh(visorGeom, visorMat);
    rearVisorR.position.set(0, 0.36, -0.22);
    rearVisorR.rotation.y = Math.PI;
    const rearVisorY = new THREE.Mesh(visorGeom, visorMat);
    rearVisorY.position.set(0, 0.0, -0.22);
    rearVisorY.rotation.y = Math.PI;
    const rearVisorG = new THREE.Mesh(visorGeom, visorMat);
    rearVisorG.position.set(0, -0.36, -0.22);
    rearVisorG.rotation.y = Math.PI;

    head.add(
      redMesh, yellowMesh, greenMesh,
      rearRed, rearYellow, rearGreen,
      visorR, visorY, visorG,
      rearVisorR, rearVisorY, rearVisorG
    );

    this.redLenses.push(redMesh, rearRed);
    this.yellowLenses.push(yellowMesh, rearYellow);
    this.greenLenses.push(greenMesh, rearGreen);

    if (isOverhead) {
      this.overheadRedMesh = redMesh;
      this.overheadYellowMesh = yellowMesh;
      this.overheadGreenMesh = greenMesh;
    } else if (!this.redMesh) {
      this.redMesh = redMesh;
      this.yellowMesh = yellowMesh;
      this.greenMesh = greenMesh;
    }

    return head;
  }

  public update(deltaTime: number) {
    this.timer += deltaTime;
    const cycleTime = 32; // Total cycle length in seconds
    const t = this.timer % cycleTime;

    if (t < 13) {
      this.phase = 'RED';
    } else if (t < 15) {
      this.phase = 'RED_YELLOW'; // Standard German pre-green phase
    } else if (t < 28) {
      this.phase = 'GREEN';
    } else {
      this.phase = 'YELLOW';
    }

    this.updateLights();
  }

  private updateLights() {
    const isRed = this.phase === 'RED' || this.phase === 'RED_YELLOW';
    const isYellow = this.phase === 'RED_YELLOW' || this.phase === 'YELLOW';
    const isGreen = this.phase === 'GREEN';

    const setLens = (mesh: THREE.Mesh, active: boolean, onColor: number, offColor: number) => {
      if (!mesh) return;
      const mat = mesh.material as THREE.MeshStandardMaterial;
      mat.color.setHex(active ? onColor : offColor);
      mat.emissive.setHex(active ? onColor : 0x000000);
      mat.emissiveIntensity = active ? 4.5 : 0.0;
    };

    for (const m of this.redLenses) setLens(m, isRed, 0xef4444, 0x450a0a);
    for (const m of this.yellowLenses) setLens(m, isYellow, 0xfbbf24, 0x422006);
    for (const m of this.greenLenses) setLens(m, isGreen, 0x22c55e, 0x052e16);

    // Glow point light
    if (this.signalGlowLight) {
      if (isRed) {
        this.signalGlowLight.color.setHex(0xef4444);
        this.signalGlowLight.intensity = 3.5;
      } else if (isYellow) {
        this.signalGlowLight.color.setHex(0xfbbf24);
        this.signalGlowLight.intensity = 3.0;
      } else if (isGreen) {
        this.signalGlowLight.color.setHex(0x22c55e);
        this.signalGlowLight.intensity = 3.5;
      }
    }
  }
}

